import { useEffect, useState } from 'react';
import { DangerousHtmlContent } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import type { FareRule, PriceFormat } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useHistory } from 'react-router-dom';
import { price } from '@eti/utils';
import { routes } from '../../../../constants/routesAndApisConstants';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../utils/carriersUtils';
import { linkStyles } from '../../../../utils/styleUtils';
import type { BoundsType } from '../../models';
import FareRulesOverlay from './overlays/FareRulesOverlay';
import RefundProcessOverlay from './overlays/RefundProcessOverlay';
import RyanAirVerificationProcessOverlay from './overlays/RyanairVerificationProcessOverlay';

const textsWrapperStyles = css`
  font-size: 0.75rem;
`;

export const buttonStyles = css`
  all: unset;
  appearance: none;
  cursor: pointer;
  text-decoration: underline;
  ${linkStyles}
`;

type Texts = {
  fareRulesLinkText: string;
  paymentConditionsText: string;
  travelConditions: string;
  termsConditions: string;
  lccConditionsLinks: string;
  lccConditionsText: string;
  refundProcessText: string;
  paymentTermsConditionsWithLinksText: string;
};

const getTextContent = ({
  shouldShowSplitTermsConditionsText = false,
  fareRulesLinkText,
  paymentConditionsText,
  paymentTermsConditionsWithLinksText,
  travelConditions,
  termsConditions,
}: {
  shouldShowSplitTermsConditionsText?: boolean;
  fareRulesLinkText: string;
  paymentConditionsText: string;
  paymentTermsConditionsWithLinksText: string;
  travelConditions: string;
  termsConditions: string;
}) => {
  const source = shouldShowSplitTermsConditionsText
    ? paymentTermsConditionsWithLinksText
    : paymentConditionsText;

  return source
    .replace('{TAC}', travelConditions)
    .replace('{TEC}', termsConditions)
    .replace(
      '{FR}',
      `<button aria-haspopup="dialog" class="${buttonStyles}" data-fareruleslink>${fareRulesLinkText}</button>`,
    );
};

const generateConditionsText = (
  {
    fareRulesLinkText,
    paymentConditionsText,
    paymentTermsConditionsWithLinksText,
    travelConditions,
    termsConditions,
    lccConditionsLinks,
    lccConditionsText,
    refundProcessText,
  }: Texts,
  shouldShowSplitTermsConditionsText?: boolean,
) => {
  const textContent = getTextContent({
    shouldShowSplitTermsConditionsText,
    fareRulesLinkText,
    paymentConditionsText,
    paymentTermsConditionsWithLinksText,
    travelConditions,
    termsConditions,
  });

  return lccConditionsLinks
    ? `${textContent} ${lccConditionsText} ${lccConditionsLinks}. ${refundProcessText}`
    : textContent;
};

const useTravelConditionsTexts = (priceInChargeCurrency: string) => {
  const { t } = useTranslation();
  const history = useHistory();
  const refundText = t('Payment.Conditions.RefundText');
  const refundLink = t('Payment.Conditions.RefundProcessLink');
  const refundProcessText = refundText.replace(
    '{RP}',
    `<button aria-haspopup="dialog" class="${buttonStyles}" data-refundprocesslink>${refundLink}</button>`,
  );
  return {
    fareRulesHeaderText: t('Payment.FareRules.Modal.HeaderText'),
    fareRulesLinkText: t('Payment.FareRules.Link.Text'),
    lccConditionsText: t('Payment.TravelConditionsText.Lcc'),
    otherChargeCurrencyText: t(
      'PaymentPage.TravelConditionsAccept.OtherChargeCurrency',
      priceInChargeCurrency,
    ),
    paymentConditionsAdditionalText: t('Payment.Conditions.AdditionalText'),
    paymentConditionsText: t('Payment.Conditions.Text'),
    paymentTermsConditionsWithLinksText: t('Payment.TermsConditionsWithLinks.Text'),
    refundProcessText,
    refundProcessHeaderText: t('Payment.Conditions.RefundHeader'),
    refundProcessDescription: t('Payment.Conditions.RefundDescription'),
    termsConditions: t('Payment.TermsConditions.Text'),
    travelConditions:
      history?.location?.pathname === routes.PAYMENT_LINK
        ? t('PaymentLink.TravelConditions.Text')
        : t('Payment.TravelConditions.Text'),
    ryanairVerificationProcessHeaderText: t('PaymentPage.RyanairVerificationProcess.Drawer.Title'),
    ryanairVerificationProcessDescription: t('PaymentPage.RyanairVerificationProcess.Drawer.Text'),
    carrierFlightConfirmationText: t(
      'PaymentPage.CarrierFlightConfirmation.TermsAndConditions.Text',
    ),
  };
};

interface TravelConditionsTextProps {
  bounds: BoundsType;
  chargeCurrencyPriceFormat?: PriceFormat;
  fareRules: FareRule[];
  fareRulesDescription: string;
  isOtherChargeCurrency?: boolean;
  lccConditionsLinks: string;
  priceInChargeCurrency?: number;
  shouldSplitText?: boolean;
}

const TravelConditionsText = ({
  bounds,
  chargeCurrencyPriceFormat,
  fareRules,
  fareRulesDescription,
  isOtherChargeCurrency = false,
  lccConditionsLinks,
  priceInChargeCurrency,
  shouldSplitText = false,
}: TravelConditionsTextProps) => {
  const { p } = useProperty();
  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );
  const shouldShowCarrierFlightConfirmationText = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const [isOpen, setIsOpen] = useState(false);
  const [showRefundProcessInfo, setShowRefundProcessInfo] = useState(false);
  const [showRyanairVerificationProcessInfo, setShowRyanairVerificationProcessInfo] =
    useState(false);

  const chargeCurrencyPriceFormatter =
    isOtherChargeCurrency &&
    chargeCurrencyPriceFormat &&
    price.createPriceFormatter(chargeCurrencyPriceFormat);
  const texts = useTravelConditionsTexts(
    chargeCurrencyPriceFormatter
      ? chargeCurrencyPriceFormatter.formatPrice(priceInChargeCurrency ?? 0)
      : '',
  );

  useEffect(() => {
    const fareRuleLink = document.querySelector<HTMLButtonElement>('[data-fareRulesLink]');
    const refundProcessLink = document.querySelector<HTMLButtonElement>('[data-refundprocesslink]');
    const ryanairVerificationProcessLink = document.querySelector<HTMLLinkElement>(
      '[data-ryanairverificationprocesslink]',
    );
    if (fareRuleLink) {
      fareRuleLink.onclick = (event) => {
        event.preventDefault();
        setIsOpen(true);
      };
    }
    if (refundProcessLink) {
      refundProcessLink.onclick = (event) => {
        event.preventDefault();
        setShowRefundProcessInfo(true);
      };
    }
    if (ryanairVerificationProcessLink) {
      ryanairVerificationProcessLink.onclick = (event) => {
        event.preventDefault();
        setShowRyanairVerificationProcessInfo(true);
      };
    }
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRefundDrawerClose = () => {
    setShowRefundProcessInfo(false);
  };

  const content = generateConditionsText({ lccConditionsLinks, ...texts }, shouldSplitText);

  return (
    <div className={textsWrapperStyles}>
      <DangerousHtmlContent content={content} />
      {texts.paymentConditionsAdditionalText && (
        <DangerousHtmlContent content={texts.paymentConditionsAdditionalText} />
      )}
      {texts.carrierFlightConfirmationText &&
        shouldShowCarrierFlightConfirmationText &&
        isRyanAirTicketingOrMarketingCarrierPresent && (
          <DangerousHtmlContent content={texts.carrierFlightConfirmationText} />
        )}
      {isOtherChargeCurrency && (
        <span data-testid="travel-conditions-other-charge-currency-text">
          {texts.otherChargeCurrencyText}
        </span>
      )}
      {texts.fareRulesLinkText && (
        <FareRulesOverlay
          fareRules={fareRules}
          fareRulesDescription={fareRulesDescription}
          fareRulesHeaderText={texts.fareRulesHeaderText}
          isOpen={isOpen}
          onClose={handleClose}
        />
      )}
      {texts.refundProcessText && (
        <RefundProcessOverlay
          isOpen={showRefundProcessInfo}
          onClose={handleRefundDrawerClose}
          refundProcessDescription={texts.refundProcessDescription}
          refundProcessHeaderText={texts.refundProcessHeaderText}
        />
      )}
      {texts.ryanairVerificationProcessHeaderText && (
        <RyanAirVerificationProcessOverlay
          isOpen={showRyanairVerificationProcessInfo}
          onClose={() => setShowRyanairVerificationProcessInfo(false)}
          ryanairVerificationProcessDescription={texts.ryanairVerificationProcessDescription}
          ryanairVerificationProcessHeaderText={texts.ryanairVerificationProcessHeaderText}
        />
      )}
    </div>
  );
};

export default TravelConditionsText;
